import { Fragment, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { api } from "../api";
import { compactNumber } from "../format";
import { describeDates } from "../../shared/arxivId";
import { highlightSegments } from "../../shared/matcher";
import type { Keyword, MatchField, MatchedPaper, PapersResponse, Recipient, Status } from "../../shared/types";

const PAGE_SIZE = 25;

function OriginTag({ origin }: { origin: string }) {
  if (origin === "live") return null;
  const label = origin === "backfill" ? "history" : "revision";
  return (
    <span className="chip chip-static" style={{ padding: "2px 6px", fontSize: 10.5 }}>
      {label}
    </span>
  );
}

/** Renders `text` with every hit of `terms` marked, using the matcher's own rules. */
function Highlight({ text, terms }: { text: string; terms: string[] }) {
  return (
    <>
      {highlightSegments(text, terms).map((segment, index) =>
        segment.hit ? (
          <mark className="hit" key={index}>
            {segment.text}
          </mark>
        ) : (
          <Fragment key={index}>{segment.text}</Fragment>
        ),
      )}
    </>
  );
}

const FIELD_LABEL: Record<MatchField, string> = {
  title: "title",
  abstract: "abstract",
  authors: "authors",
  comments: "comments",
  body: "full text",
};

/** Fields the row already shows in full, so a hit there is visible in place. */
const SHOWN_IN_PLACE = new Set<MatchField>(["title", "abstract", "authors"]);

// Same deliberately permissive rule as the Worker, so the form flags exactly
// what the API would reject.
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;.]+(\.[^\s@,;.]+)+$/;
const MAX_SHARE = 20;

function parseEmails(raw: string): string[] {
  return [...new Set(raw.split(/[\s,;]+/).map((part) => part.trim().toLowerCase()).filter(Boolean))];
}

function SharePaper({
  arxivId,
  recipients,
  onSent,
  onCancel,
}: {
  arxivId: string;
  recipients: Recipient[];
  onSent: (message: string) => void;
  onCancel: () => void;
}) {
  const [raw, setRaw] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emails = parseEmails(raw);
  const invalid = emails.filter((email) => !EMAIL_RE.test(email));
  const suggestions = recipients.filter((recipient) => recipient.active && !emails.includes(recipient.email));

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (emails.length === 0 || invalid.length > 0 || emails.length > MAX_SHARE) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.sharePaper(arxivId, emails, note.trim());
      const refused = result.rejected.map((entry) => entry.email);
      onSent(
        `Sent to ${result.delivered} address${result.delivered === 1 ? "" : "es"}` +
          (refused.length > 0 ? ` · refused: ${refused.join(", ")}` : ""),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not send");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="share" onSubmit={(event) => void submit(event)}>
      <textarea
        className="input"
        rows={2}
        value={raw}
        placeholder="Email addresses, separated by commas or new lines"
        onChange={(event) => setRaw(event.target.value)}
        autoFocus
      />
      {suggestions.length > 0 ? (
        <div className="chips">
          {suggestions.map((recipient) => (
            <button
              type="button"
              className="chip"
              key={recipient.id}
              onClick={() => setRaw((current) => (current.trim() ? `${current.trim()}, ` : "") + recipient.email)}
            >
              + {recipient.email}
            </button>
          ))}
        </div>
      ) : null}
      <textarea
        className="input"
        rows={2}
        value={note}
        maxLength={1000}
        placeholder="Optional note"
        onChange={(event) => setNote(event.target.value)}
      />
      {invalid.length > 0 ? <div className="error">Not a valid address: {invalid.join(", ")}</div> : null}
      {emails.length > MAX_SHARE ? <div className="error">At most {MAX_SHARE} addresses per send</div> : null}
      {error ? <div className="error">{error}</div> : null}
      <div className="share-actions">
        <button
          className="btn btn-primary"
          type="submit"
          disabled={busy || emails.length === 0 || invalid.length > 0 || emails.length > MAX_SHARE}
        >
          {busy ? "Sending…" : emails.length > 1 ? `Send to ${emails.length}` : "Send"}
        </button>
        <button className="btn" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function PaperRow({ paper, recipients }: { paper: MatchedPaper; recipients: Recipient[] }) {
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const terms = paper.keywords.map((keyword) => keyword.term);
  const hidden = paper.keywords.filter(
    (keyword) => keyword.snippet !== null && keyword.field !== null && !SHOWN_IN_PLACE.has(keyword.field),
  );

  return (
    <div className="paper">
      <a className="paper-title" href={paper.url} target="_blank" rel="noreferrer">
        <Highlight text={paper.title} terms={terms} />
      </a>
      {paper.authors ? (
        <div className="paper-authors">
          <Highlight text={paper.authors} terms={terms} />
        </div>
      ) : null}
      <div className="paper-meta">
        <span>{paper.arxivId}</span>
        <span>·</span>
        <span>{paper.categories}</span>
        <span>·</span>
        <span>{describeDates(paper.arxivId, paper.createdDate)}</span>
        {paper.updatedDate && paper.updatedDate !== paper.createdDate ? (
          <>
            <span>·</span>
            <span>revised {paper.updatedDate}</span>
          </>
        ) : null}
      </div>
      {paper.abstract ? (
        <div
          className={open ? "paper-abstract" : "paper-abstract clamp"}
          onClick={() => setOpen((value) => !value)}
          title={open ? "Collapse" : "Expand"}
        >
          <Highlight text={paper.abstract} terms={terms} />
        </div>
      ) : null}
      {hidden.map((keyword) => (
        <div className="paper-where" key={keyword.id}>
          <span className="paper-where-label">
            {keyword.term} · in {FIELD_LABEL[keyword.field as MatchField]}
          </span>
          <Highlight text={keyword.snippet as string} terms={[keyword.term]} />
        </div>
      ))}
      <div className="paper-actions">
        <div className="chips">
          {paper.keywords.map((keyword) => (
            <span className="chip chip-static chip-on" key={keyword.id}>
              {keyword.term}
              {keyword.field ? <span className="chip-field">· {FIELD_LABEL[keyword.field]}</span> : null}
              <OriginTag origin={keyword.origin} />
            </span>
          ))}
          {paper.emailPending ? (
            <span className="chip chip-static" style={{ borderColor: "var(--accent2)", color: "var(--accent2)" }}>
              email queued
            </span>
          ) : null}
        </div>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {sent && !sharing ? <span className="ok">{sent}</span> : null}
          <button
            className="btn btn-tiny"
            type="button"
            onClick={() => {
              setSent(null);
              setSharing((value) => !value);
            }}
          >
            {sharing ? "close" : "email"}
          </button>
        </span>
      </div>
      {sharing ? (
        <SharePaper
          arxivId={paper.arxivId}
          recipients={recipients}
          onCancel={() => setSharing(false)}
          onSent={(message) => {
            setSent(message);
            setSharing(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * An empty result has three very different causes, and saying "No matches yet"
 * for all of them makes correct behaviour look broken — the corpus may simply
 * not be harvested yet, or the term may genuinely be absent from arXiv.
 */
function EmptyState({ keywords, status, keywordId }: { keywords: Keyword[]; status: Status | null; keywordId: number | null }) {
  if (keywords.length === 0) return <div className="empty">Add a keyword to start matching</div>;

  const term = keywords.find((keyword) => keyword.id === keywordId)?.term ?? null;

  if (status?.harvest.mode === "seeding") {
    const back = status.harvest.seedCursor;
    return (
      <div className="empty">
        Still seeding arXiv history — {compactNumber(status.counts.papers)} papers so far.
        <br />
        {back ? `Newest first; covered back to ${back}.` : "Newest papers are ingested first."} Older
        matches appear as the 30-day backlog fills.
      </div>
    );
  }

  return (
    <div className="empty">
      {term ? `Nothing matched "${term}" in the last 30 days` : "No matches yet"}
      <br />
      Monitoring continues; new papers are checked every 15 minutes.
    </div>
  );
}

export function PapersPanel({
  keywords,
  recipients,
  refreshToken,
  status,
}: {
  keywords: Keyword[];
  recipients: Recipient[];
  refreshToken: number;
  status: Status | null;
}) {
  const [keywordId, setKeywordId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState<PapersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Any filter change invalidates the current page number.
  useEffect(() => setPage(0), [keywordId, debounced]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .papers({ keywordId, search: debounced, limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : "Could not load papers");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [keywordId, debounced, page, refreshToken]);

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div className="panel-title">Matched papers</div>
          <div className="panel-sub">Newest match first</div>
        </div>
        <input
          className="input"
          style={{ width: 220 }}
          value={search}
          placeholder="Filter these results…"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="chips" style={{ marginTop: 13 }}>
        <button
          type="button"
          className={keywordId === null ? "chip chip-on" : "chip"}
          onClick={() => setKeywordId(null)}
        >
          all
        </button>
        {keywords.map((keyword) => (
          <button
            type="button"
            key={keyword.id}
            className={keywordId === keyword.id ? "chip chip-on" : "chip"}
            onClick={() => setKeywordId(keyword.id)}
          >
            {keyword.term}
            <span className="chip-count">{compactNumber(keyword.matchCount)}</span>
          </button>
        ))}
      </div>

      {error ? (
        <div className="error" style={{ marginTop: 13 }}>
          {error}
        </div>
      ) : null}

      <div style={{ marginTop: 6 }}>
        {loading && !data ? (
          <div className="empty">Loading…</div>
        ) : total === 0 ? (
          <EmptyState keywords={keywords} status={status} keywordId={keywordId} />
        ) : (
          (data?.papers ?? []).map((paper) => <PaperRow key={paper.arxivId} paper={paper} recipients={recipients} />)
        )}
      </div>

      {total > PAGE_SIZE ? (
        <div className="pager">
          <button className="btn btn-tiny" type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            ← prev
          </button>
          <span>
            {page + 1} / {pages} · {compactNumber(total)} papers
          </span>
          <button
            className="btn btn-tiny"
            type="button"
            disabled={page + 1 >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            next →
          </button>
        </div>
      ) : null}
    </div>
  );
}
